import { components } from "../../api/types";
import {
  getActualAge,
  getActualSex,
  getActualRace,
  getActualEthnicity,
  getActualDiagnosisMask,
  getActualScores,
  getActualPercentileRanks,
  getPredictedPercentileRanks,
  getTakenMeasureMask,
  getPredictedMeans,
  getPredictedStds,
  getAllDiagnosisLabels,
  getDsmAssociations,
  setSelectedIdentifier,
  getPosteriors,
} from "../../api/calls";

export type Response = {
  actualAge: number,
  actualSex: string,
  actualRace: string | null,
  actualEthnicity: string | null,
  actualDiagnosisMask: boolean[],
  takenMeasureMask: boolean[],
  actualScores: (number | null)[],
  actualPercentileRanks: (number | null)[],
  predictedPercentileRanks: (number | null)[],
  posteriors: number[],
  predictedMeans: number[],
  predictedStds: number[],
  allDiagnosisLabels: (string | null)[],
  dsmAssociations: components["schemas"]["DsmEntry"][],
};

/**
 * Call when the userId changes.
 * 1) Tell backend who the selected identifier is
 * 2) Fetch everything user-scoped in parallel
 * 
 * @param userId User ID
 * @param signal Abort signal
 * @returns User bundle
 */
export const loadUserBundle = async (
  userId: string,
  signal?: AbortSignal,
): Promise<Response> => {
  /* Set selected user (server keeps it in session/state) */
  await setSelectedIdentifier({ identifier: userId }, signal);

  /* Fetch */
  const [
    actualAge,
    actualSex,
    actualRace,
    actualEthnicity,
    actualDiagnosisMask,
    takenMeasureMask,
    actualScores,
    actualPercentileRanks,
    predictedPercentileRanks,
    posteriors,
    predictedMeans,
    predictedStds,
    allDiagnosisLabels,
    dsmAssociations,
  ] = await Promise.all([
    getActualAge(signal),
    getActualSex(signal),
    getActualRace(signal),
    getActualEthnicity(signal),
    getActualDiagnosisMask(signal),
    getTakenMeasureMask(signal),
    getActualScores(signal),
    getActualPercentileRanks({filterParams: {}}, signal),
    getPredictedPercentileRanks({filterParams: {}}, signal),
    getPosteriors(signal),
    getPredictedMeans(signal),
    getPredictedStds(signal),
    getAllDiagnosisLabels(signal),
    getDsmAssociations(signal),
  ]);

  return {
    actualAge,
    actualSex,
    actualRace,
    actualEthnicity,
    actualDiagnosisMask,
    takenMeasureMask,
    actualScores,
    actualPercentileRanks,
    predictedPercentileRanks,
    posteriors,
    predictedMeans,
    predictedStds,
    allDiagnosisLabels,
    dsmAssociations,
  };
};
